import React, { useEffect, useState } from 'react';
import { usersService, type User, type UserUpdateInput } from '../../services/usersService';

interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
  user: User | null;
}

const EditUserModal: React.FC<EditUserModalProps> = ({ isOpen, onClose, onSaved, user }) => {
  const [formData, setFormData] = useState<UserUpdateInput>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        role: user.role,
        global_role: user.global_role,
        active: user.active,
      });
    }
  }, [user]);

  if (!isOpen || !user) return null;

  const handleChange = (field: keyof UserUpdateInput, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value as never }));
    if (errors[field as string]) setErrors(prev => ({ ...prev, [field as string]: '' }));
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!formData.email) e.email = 'Email is required';
    if (!formData.first_name) e.first_name = 'First name is required';
    if (!formData.last_name) e.last_name = 'Last name is required';
    if (!formData.role) e.role = 'Role is required';
    if (!formData.global_role) e.global_role = 'Global role is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (evt: React.FormEvent) => {
    evt.preventDefault();
    if (!validate()) return;
    setIsSaving(true);
    try {
      await usersService.update(user.id, formData);
      onSaved?.();
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Edit User</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 focus:outline-none">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input value={formData.email || ''} onChange={e => handleChange('email', e.target.value)} type="email" className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.email ? 'border-red-300' : 'border-gray-300'}`}/>
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
              <input value={formData.first_name || ''} onChange={e => handleChange('first_name', e.target.value)} className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.first_name ? 'border-red-300' : 'border-gray-300'}`}/>
              {errors.first_name && <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
              <input value={formData.last_name || ''} onChange={e => handleChange('last_name', e.target.value)} className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.last_name ? 'border-red-300' : 'border-gray-300'}`}/>
              {errors.last_name && <p className="text-red-500 text-xs mt-1">{errors.last_name}</p>}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
              <select value={formData.role || 'user'} onChange={e => handleChange('role', e.target.value)} className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.role ? 'border-red-300' : 'border-gray-300'}`}>
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
              {errors.role && <p className="text-red-500 text-xs mt-1">{errors.role}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Global Role *</label>
              <select value={formData.global_role || 'user'} onChange={e => handleChange('global_role', e.target.value)} className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.global_role ? 'border-red-300' : 'border-gray-300'}`}>
                <option value="user">user</option>
                <option value="internal_admin">internal_admin</option>
                <option value="super_admin">super_admin</option>
              </select>
              {errors.global_role && <p className="text-red-500 text-xs mt-1">{errors.global_role}</p>}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Active</label>
            <div className="flex items-center space-x-3">
              <button type="button" onClick={() => handleChange('active', !formData.active)} className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${formData.active ? 'bg-blue-600' : 'bg-gray-200'}`}>
                <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${formData.active ? 'translate-x-5' : 'translate-x-0'}`}/>
              </button>
              <span className="text-sm text-gray-600">{formData.active ? 'Enabled' : 'Disabled'}</span>
            </div>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors">Cancel</button>
            <button type="submit" disabled={isSaving} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded-md transition-colors">{isSaving ? 'Saving...' : 'Update Data'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUserModal;


